<template>
  <div class="tw-flex tw-h-full tw-flex-col tw-bg-white">
    <header
      class="
        tw-flex
        tw-h-[60px]
        tw-items-center
        tw-justify-between
        tw-gap-2
        tw-px-4
        tw-shrink-0
        tw-border-solid
        tw-border-x-0
        tw-border-t-0
        tw-border-b
        tw-border-sf-slate-lighter-1
      "
    >
      <div class="tw-flex tw-min-w-0 tw-items-center tw-gap-2">
        <h3
          class="
            tw-my-0
            tw-truncate
            tw-text-base
            tw-font-bold
            tw-text-sf-slate-dark-2
          "
        >
          {{ I18n.t('companies_overview.assistant.title') }}
        </h3>
        <span
          class="
            tw-inline-flex
            tw-items-center
            tw-gap-1
            tw-rounded-full
            tw-border
            tw-border-solid
            tw-border-sf-info-lighter-1
            tw-bg-sf-info-subtle-1
            tw-px-1.5
            tw-py-0.5
            tw-text-xs
            tw-uppercase
            tw-text-sf-info-dark-1
          "
          data-testid="assistant-release-badge"
        >
          <i class="fa fa-solid fa-circle-dot tw-text-[10px]" />
          {{ I18n.t('companies_overview.assistant.beta') }}
        </span>
      </div>

      <div class="tw-flex tw-shrink-0 tw-items-center tw-gap-2">
        <SfButtonDs
          v-if="hasConversation"
          v-tooltip.placementBottom="
            I18n.t('companies_overview.assistant.start_new_tooltip')
          "
          variant="tertiary"
          icon-before="rotate-right"
          data-testid="assistant-start-new"
          @click="startNew"
        >
          {{ I18n.t('companies_overview.assistant.start_new') }}
        </SfButtonDs>
        <SfButtonDs
          variant="tertiary"
          icon-only
          icon-before="xmark"
          :aria-label="I18n.t('common.close')"
          @click="close"
        />
      </div>
    </header>

    <div
      ref="bodyRef"
      class="tw-flex tw-flex-1 tw-min-h-0 tw-flex-col tw-overflow-y-auto tw-p-4"
      data-testid="assistant-body"
      @scroll.passive="updateAnchor"
    >
      <AssistantEmptyState v-if="!hasConversation" @select="submitSuggestion" />
      <AssistantConversation v-else />
    </div>

    <footer
      class="
        tw-shrink-0
        tw-border-solid
        tw-border-x-0
        tw-border-b-0
        tw-border-t
        tw-border-sf-slate-lighter-1
        tw-bg-white
        tw-p-4
      "
    >
      <div class="tw-flex tw-flex-col tw-gap-3">
        <AssistantInput
          :model-value="draft"
          :loading="loading"
          @update:model-value="setDraft"
          @submit="submitPrompt"
          @cancel="cancel"
        />
        <p
          class="
            tw-m-0 tw-px-4 tw-text-center tw-text-xs tw-text-sf-slate-light-2
          "
        >
          {{ I18n.t('companies_overview.assistant.disclaimer') }}
        </p>
      </div>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { nextTick, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useEventListener } from '@vueuse/core'
import I18n from '@app/adapters/I18n'
import { useDrawer } from '@app/shared/pinia/drawer'
import { useAssistantStore } from '@app/insights/store/assistant'
import SfButtonDs from '@app/shared/vue/components/sf-button/sf-button-ds.vue'
import AssistantEmptyState from './assistant/assistant-empty-state.vue'
import AssistantConversation from './assistant/assistant-conversation.vue'
import AssistantInput from './assistant/assistant-input.vue'

const drawerStore = useDrawer()
const assistantStore = useAssistantStore()

const { messages, loading, streamingText, hasConversation, draft } =
  storeToRefs(assistantStore)
const { startNew, submitSuggestion, cancel, setDraft, submitPrompt } =
  assistantStore

const bodyRef = ref<HTMLElement | null>(null)

// Whether the user is reading at (or near) the bottom. Streamed reply growth
// only keeps the view pinned while this holds, so scrolling up to re-read an
// earlier message is never fought mid-generation.
const anchoredToBottom = ref(true)
const ANCHOR_THRESHOLD_PX = 80

function scrollToBottom(): void {
  const el = bodyRef.value
  if (!el) return
  el.scrollTop = el.scrollHeight
  anchoredToBottom.value = true
}

function updateAnchor(): void {
  const el = bodyRef.value
  if (!el) return
  anchoredToBottom.value =
    el.scrollHeight - el.scrollTop - el.clientHeight <= ANCHOR_THRESHOLD_PX
}

// A new message (the user's, or the agent's finished reply) and the loading
// row must always end up visible: snap to the bottom regardless of position.
watch(
  [() => messages.value.length, loading],
  () => void nextTick(scrollToBottom)
)

// The streamed reply grows the conversation without adding a message; keep
// the view pinned so the text stays in view while it is being generated.
watch(streamingText, () => {
  if (anchoredToBottom.value) void nextTick(scrollToBottom)
})

// Esc cancels an in-flight reply regardless of where focus sits in the drawer.
useEventListener(document, 'keydown', (event: KeyboardEvent) => {
  if (event.key === 'Escape' && loading.value) {
    event.preventDefault()
    cancel()
  }
})

function close(): void {
  drawerStore.closeRightDrawerWithoutPersisting(false)
}
</script>
